import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { trackPageView, GA_TRACKING_ID } from '@/lib/googleAnalytics';
import { useBrandingSettings } from '@/contexts/AppSettingsContext';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

interface AnalyticsProviderProps {
  children: React.ReactNode;
  trackingId?: string;
  enabled?: boolean;
}

/**
 * AnalyticsProvider handles SPA page view tracking.
 * Google Analytics script loading is handled by ConsentAwareAnalytics.
 * This component only tracks page views after GA is loaded and consent is granted.
 */
const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  trackingId,
  enabled = true
}) => {
  const [location] = useLocation();
  const { settings } = useBrandingSettings();
  const { canUseAnalytics } = useCookieConsent();

  // Use dynamic settings or fallback to environment variable
  const dynamicTrackingId = trackingId || settings?.google_analytics_id || GA_TRACKING_ID;

  useEffect(() => {
    // Safety checks: only track if enabled, has tracking ID, and consent granted
    if (!enabled || !dynamicTrackingId) return;
    
    if (!canUseAnalytics()) {
      // Consent not granted - don't track
      return;
    }

    // Check if gtag is available (GA must be loaded)
    if (typeof window === 'undefined' || !window.gtag) {
      // GA not loaded yet - wait a bit and try again
      const checkInterval = setInterval(() => {
        if (window.gtag) {
          clearInterval(checkInterval);
          // Track initial page view
          trackPageView(window.location.href, document.title, dynamicTrackingId);
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => clearInterval(checkInterval), 5000);

      return () => clearInterval(checkInterval);
    }

    // Track page views on route changes (SPA routing)
    // Small delay to ensure page is fully rendered
    const timeoutId = setTimeout(() => {
      trackPageView(window.location.href, document.title, dynamicTrackingId);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location, enabled, dynamicTrackingId, canUseAnalytics]);

  return <>{children}</>;
};

export default AnalyticsProvider;
